import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNotification } from '@eye-able/ui';
import { api } from '@http/constants';
import fetchWrapper from '@/http/fetchWrapper';
import { useInlineTranslation } from '@/i18n/hooks/useInlineTranslation';
import type {
  GovernanceIssueResponse,
  GovernanceIssueStatus,
} from '@http/queries/useFetchGovernanceIssues';

export type UpdatableGovernanceIssueStatus = Exclude<
  GovernanceIssueStatus,
  'stale'
>;

export interface UpdateGovernanceIssueStatusBody {
  id: number;
  status: UpdatableGovernanceIssueStatus;
}

const statusNotificationKeys: Record<UpdatableGovernanceIssueStatus, string> = {
  fixed: 'governanceIssueMarkedFixedSuccess',
  ignored: 'governanceIssueIgnoredSuccess',
  open: 'governanceIssueReopenedSuccess',
};

/** Mark a governance issue as open, fixed, or ignored. */
export function useUpdateGovernanceIssueStatus() {
  const queryClient = useQueryClient();
  const notification = useNotification();
  const { t } = useInlineTranslation();

  return useMutation({
    mutationFn: (body: UpdateGovernanceIssueStatusBody) =>
      fetchWrapper.put<GovernanceIssueResponse>(api.governanceIssues, body),
    onSuccess: async (issue, updatedIssue) => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: [api.governanceIssues] }),
        queryClient.invalidateQueries({ queryKey: [api.policies] }),
        queryClient.invalidateQueries({ queryKey: [api.governanceOverview] }),
      ]);
      notification.success(t(statusNotificationKeys[updatedIssue.status]));
      return issue;
    },
  });
}
